import React from 'react';
import PropTypes from 'prop-types';
import {Notification as PepsicoNotification} from '@pepsico-ds/ui';
import {Icon} from '@/components/atoms';

/**
 * A small atom wrapper around pepsico-ds Notification to standardize
 * primary action rendering as a rounded icon button and to pass-through other props.
 * @param {Object} props - The properties object.
 * @param {string} [props.className=''] - Additional CSS class names to apply to the notification.
 * @param {string|JSX.Element} [props.primaryLabel=''] - The label for the primary action button.
 * @param {string} [props.primaryActionIcon] - The icon name for the primary action button.
 * @param {Function} [props.primaryActionOnClick] - The click handler for the primary action button.
 * @param {string|JSX.Element} [props.secondaryActionLabel] - The label for the secondary action button.
 * @param {Function} [props.secondaryActionOnClick] - The click handler for the secondary action button.
 * @param {boolean} [props.showActionButtons] - Flag to determine if action buttons should be shown.
 * @param {boolean} [props.showIcon] - Flag to determine if the notification icon should be shown.
 * @param {boolean} [props.showText] -- Flag to determine if the notification text should be shown.
 * @param {boolean} [props.showTitle] - Flag to determine if the notification title should be shown.
 * @param {string} [props.text] - The text content of the notification.
 * @param {string} [props.title] - The title of the notification.
 * @param {string} [props.type] - The type of the notification (e.g., 'info', 'success', 'warning', 'error').
 * @returns {JSX.Element} The rendered Notification component.
 */
const Notification = ({
  className = '',
  primaryLabel = '',
  primaryActionIcon,
  primaryActionOnClick,
  secondaryActionLabel,
  secondaryActionOnClick,
  showActionButtons,
  showIcon,
  showText,
  showTitle,
  text,
  title,
  type,
  ...rest
}) => {
  const primaryActionLabel = primaryActionIcon ? (
    <div className='icon-wrapper inline-flex h-8 w-8 items-center justify-center rounded border'>
      <Icon icon={primaryActionIcon} size='small' />
    </div>
  ) : (
    primaryLabel
  );

  return (
    <PepsicoNotification
      className={className}
      primaryActionLabel={primaryActionLabel}
      primaryActionOnClick={primaryActionOnClick}
      secondaryActionLabel={secondaryActionLabel}
      secondaryActionOnClick={secondaryActionOnClick}
      showActionButtons={showActionButtons}
      showIcon={showIcon}
      showText={showText}
      showTitle={showTitle}
      style={{}}
      text={text}
      title={title}
      type={type}
      {...rest}
    />
  );
};

// Define prop types for the Notification component run-time type checking
Notification.propTypes = {
  className: PropTypes.string,
  primaryLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  primaryActionIcon: PropTypes.string,
  primaryActionOnClick: PropTypes.func,
  secondaryActionLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  secondaryActionOnClick: PropTypes.func,
  showActionButtons: PropTypes.bool,
  showIcon: PropTypes.bool,
  showText: PropTypes.bool,
  showTitle: PropTypes.bool,
  text: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string
};

export default React.memo(Notification);
